import React, { useMemo, useState } from 'react';
import ClassNames from 'classnames';
import DurationComponent from '../Duration';
import DownloadIcon from '../icons/download.svg';
import FullscreenIcon from '../icons/fullscreen.svg';
import ExitFullscreenIcon from '../icons/fullscreen_exit.svg';
import PauseIcon from '../icons/pause.svg';
import PlayArrowIcon from '../icons/play_arrow.svg';
import ResetIcon from '../icons/refresh.svg';
import { secondsToTime } from '../utils';
import CircularProgress from './CircularProgress';
import '../index.scss';

const speeds = [
  {
    value: 1,
    title: '1x',
  },
  {
    value: 2,
    title: '2x',
  },
  {
    value: 4,
    title: '4x',
  },
  {
    value: 8,
    title: '8x',
  },
  {
    value: 16,
    title: '16x',
  },
];

export const PlaybackController = ({
  variantPage,
  video,
  progress,
  canvasRef,
  isFullScreen,
  isDownloading,
  permission,
  onPlay,
  onPause,
  onSeekChange,
  onSpeedChange,
  onDownload,
  onFullscreen,
  onReplay,
  streamMode = null,
  isRequestingDownload = false,
  downloadProgress = null,
  type = '',
  hasError = false,
}) => {
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const isHovering = useMemo(() => hoverTime !== null, [hoverTime]);

  const handleMouseMove = (e) => {
    const rect = progress.current.getBoundingClientRect();

    const percent = (e.clientX - rect.left) / rect.width;

    const clamped = Math.max(0, Math.min(1, percent));

    setHoverPosition(clamped * 100);

    setHoverTime(clamped * video.duration);
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
  };

  return (
    <>
      <div className="bottom-control-player">
        {video.playing ? (
          <button className="pause" onClick={onPause}>
            <img src={PauseIcon} alt="pause" />
          </button>
        ) : video?.ended || hasError ? (
          <button className="reset" onClick={onReplay}>
            <img src={ResetIcon} alt="reset" />
          </button>
        ) : (
          <button className="play" onClick={onPlay}>
            <img src={PlayArrowIcon} alt="play" />
          </button>
        )}

        <div className="progress-bar-wrapper">
          {(variantPage === 'camera' || variantPage === 'dashboard') && (
            <div
              className="progress"
              onClick={onSeekChange}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              ref={progress}
            >
              <div
                className="progress-filled"
                style={{ width: `${(video.played || 0) * 100}%` }}
              />

              <div
                className="progress-point"
                style={{ left: `${(video.played || 0) * 100}%` }}
              />

              {/* {!isHovering && (
                <div
                  className="timer"
                  style={{
                    left: `${(video.played || 0) * 100}%`,
                  }}
                >
                  <button className="timing">
                    <time>
                      {secondsToTime(video?.played * video?.duration)}
                    </time>
                  </button>
                </div>
              )} */}

              {isHovering && (
                <div
                  className="timer"
                  style={{
                    left: `${hoverPosition}%`,
                  }}
                >
                  <button className="timing">
                    <time>{secondsToTime(hoverTime)}</time>
                  </button>
                </div>
              )}
            </div>
          )}
          <time>
            <DurationComponent seconds={Math.round(video?.played * video.duration)} />
          </time>
        </div>

        {variantPage === 'camera' && !streamMode && (
          <div className="speed">
            <button>
              {speeds.find((s) => s.value === video.playbackRate)?.title}
            </button>
            <div className="speeds">
              {speeds.map((q, index) => (
                <React.Fragment key={`${q.value}_${index}`}>
                  <button
                    className={ClassNames({
                      selected: q.value === video.playbackRate,
                    })}
                    onClick={() => onSpeedChange(q.value)}
                  >
                    {q.title}
                  </button>
                  <br />
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {permission?.includes('download') && (
          <button
            className="download-btn"
            onClick={onDownload}
            disabled={isDownloading || isRequestingDownload}
          >
            {isRequestingDownload ? (
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid #fff',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                }}
              />
            ) : isDownloading ? (
              type === 'on_gateway' ? (
                <CircularProgress progress={downloadProgress || 0} />
              ) : (
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                  }}
                />
              )
            ) : (
              <img src={DownloadIcon} alt="download" />
            )}
          </button>
        )}

        <button className="fullscreen-btn" onClick={onFullscreen}>
          <img
            src={isFullScreen ? ExitFullscreenIcon : FullscreenIcon}
            alt="fullscreen"
          />
        </button>
      </div>
      <canvas crossOrigin="anonymous" ref={canvasRef} hidden />
    </>
  );
};
